import { useEffect, useRef, useState, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Singleton WebSocket manager to avoid re-creation on component re-mounts
let _ws = null;
let _priceMap = {};
let _listeners = new Set();
let _connected = false;
let _reconnectTimer = null;
let _pingTimer = null;

function _connect() {
  if (_ws?.readyState === WebSocket.OPEN || _ws?.readyState === WebSocket.CONNECTING) return;
  const wsUrl = BACKEND_URL.replace(/^http/, 'ws') + '/api/ws/prices';
  const ws = new WebSocket(wsUrl);

  ws.onopen = () => {
    _connected = true;
    _notifyListeners();
    _pingTimer = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) ws.send('ping');
    }, 25000);
  };

  ws.onmessage = (event) => {
    try {
      const msg = JSON.parse(event.data);
      if (msg.type === 'price_update' && Array.isArray(msg.data)) {
        msg.data.forEach(p => {
          const key = p.id || p.symbol?.toLowerCase();
          if (key) _priceMap[key] = p;
        });
        _notifyListeners();
      }
    } catch { /* ignore */ }
  };

  ws.onclose = () => {
    _connected = false;
    clearInterval(_pingTimer);
    _notifyListeners();
    _reconnectTimer = setTimeout(_connect, 2000);
  };

  ws.onerror = () => { ws.close(); };
  _ws = ws;
}

function _notifyListeners() {
  const prices = Object.values(_priceMap);
  _listeners.forEach(fn => fn({ prices, connected: _connected }));
}

export function useWebSocket() {
  const [prices, setPrices] = useState(() => Object.values(_priceMap));
  const [connected, setConnected] = useState(_connected);

  useEffect(() => {
    const handler = ({ prices: p, connected: c }) => {
      setPrices(p);
      setConnected(c);
    };
    _listeners.add(handler);
    // Start connection if not already running
    _connect();
    // Send current state immediately
    if (Object.keys(_priceMap).length > 0) {
      setPrices(Object.values(_priceMap));
    }
    return () => {
      _listeners.delete(handler);
      // Only close WS if no more listeners
      if (_listeners.size === 0 && _ws) {
        clearTimeout(_reconnectTimer);
        clearInterval(_pingTimer);
        _ws.close();
        _ws = null;
      }
    };
  }, []);

  return { prices, connected };
}
